import { styled } from "~/common/mui";
import type { CMSIframeDTO } from "../../types/dto";
import { makeExternallyStyledElement } from "../../utils/styles";
import { resolveUrl } from "~/utils/url/urlResolver";
import { fetchLmbtConfiguratorUrl } from "~/domains/product/api/fetchLmbtConfiguratorUrl";
import { useEffect, useState } from "react";

interface Props extends Omit<CMSIframeDTO, "type"> {
  className?: string;
}
const IFRAME_ID = "configuratorIframe";
const LMBT_CONFIGURATOR_SOURCE_PREFIX = "lmbt-configurator:";

export const CMSIframe = (props: Props) => {
  const { url, rootStyle, enableIframeResize } = props;
  const isLmbtConfiguratorSource = url.startsWith(LMBT_CONFIGURATOR_SOURCE_PREFIX);
  const lmbtConfiguratorCode = isLmbtConfiguratorSource
    ? url.slice(LMBT_CONFIGURATOR_SOURCE_PREFIX.length).trim()
    : undefined;
  const [resolvedLmbtUrl, setResolvedLmbtUrl] = useState<{ code: string; url: string }>();

  useEffect(() => {
    if (!lmbtConfiguratorCode) {
      return;
    }

    const abortController = new AbortController();
    void fetchLmbtConfiguratorUrl(lmbtConfiguratorCode, abortController.signal)
      .then((resolvedUrl) => {
        if (!abortController.signal.aborted) {
          setResolvedLmbtUrl({ code: lmbtConfiguratorCode, url: resolvedUrl });
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setResolvedLmbtUrl(undefined);
        }
      });
    return () => {
      abortController.abort();
    };
  }, [lmbtConfiguratorCode]);

  const configuratorUrl = isLmbtConfiguratorSource
    ? resolvedLmbtUrl && resolvedLmbtUrl.code === lmbtConfiguratorCode
      ? resolvedLmbtUrl.url
      : undefined
    : resolveUrl(url);

  useEffect(() => {
    if (enableIframeResize && configuratorUrl) {
      window.iFrameResize?.({ log: false }, `#${IFRAME_ID}`);
    }
  }, [configuratorUrl, enableIframeResize]);

  return (
    <StyledIframeContainer rootStyles={rootStyle} sx={{ "& > iframe": { width: "100%", border: "none" } }}>
      {configuratorUrl ? <iframe id={IFRAME_ID} scrolling="yes" src={configuratorUrl}></iframe> : null}
    </StyledIframeContainer>
  );
};

const Container = styled("div")({
  overflow: "hidden",
});

const StyledIframeContainer = makeExternallyStyledElement(Container);
